import React, { useEffect, useRef, useState } from 'react';
import { Button, Spin, Empty } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { REPORT_STYLE, dash, dt, printReport } from './reportStyle';

/**
 * Generic data-driven report renderer (used by the Zydus MasterLot / Lot
 * reports, which have many dynamic sections). It walks the JSON `data` object
 * and renders each top-level section in the backend report design:
 *   - object of scalars  -> key/value table
 *   - array of objects   -> table (columns = union of row keys)
 *   - object with arrays -> nested sections
 * This keeps each report in its OWN file while reusing one faithful renderer.
 *
 * Props: fetcher(params, success, onError), batchNumber, title
 */
const humanize = (k) =>
  String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const isScalar = (v) => v === null || ['string', 'number', 'boolean'].includes(typeof v);
// Same key-name heuristic used in Batch360.js's generic audit-trail renderer.
const isDateKey = (k) => /_dt$|_date$|_time$|timestamp|_at$/i.test(k);

const ScalarTable = ({ obj }) => {
  const entries = Object.entries(obj).filter(([, v]) => isScalar(v));
  if (!entries.length) return null;
  return (
    <table>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td style={{ width: '35%' }}><b>{humanize(k)}</b></td>
            <td>{isDateKey(k) ? dt(v) : dash(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const RowsTable = ({ rows }) => {
  const cols = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r || {}).forEach((k) => isScalar(r[k]) && set.add(k));
      return set;
    }, new Set())
  );
  if (!cols.length) return null;
  return (
    <table>
      <thead>
        <tr className="header-class-1">{cols.map((c) => <th key={c}>{humanize(c)}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{cols.map((c) => <td key={c} className="table_data">{isDateKey(c) ? dt(r?.[c]) : dash(r?.[c])}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
};

// Render one value (scalar / array / nested object) under a heading.
const Section = ({ name, value, level = 0 }) => {
  const HeadTag = level === 0 ? 'header-class' : 'header-class-1';
  const titleBar = (
    <div className="section-title">
      <table><tbody><tr><th className={HeadTag}>{humanize(name)}</th></tr></tbody></table>
    </div>
  );

  if (Array.isArray(value)) {
    if (!value.length) return null;
    if (value.every(isScalar)) {
      return (<>{titleBar}<table><tbody><tr><td>{value.map(dash).join(', ')}</td></tr></tbody></table></>);
    }
    return (<>{titleBar}<RowsTable rows={value} /></>);
  }

  if (value && typeof value === 'object') {
    const scalars = Object.entries(value).filter(([, v]) => isScalar(v));
    const complex = Object.entries(value).filter(([, v]) => !isScalar(v));
    return (
      <>
        {titleBar}
        {scalars.length > 0 && <ScalarTable obj={Object.fromEntries(scalars)} />}
        {complex.map(([k, v]) => <Section key={k} name={k} value={v} level={level + 1} />)}
      </>
    );
  }

  // scalar
  return (<>{titleBar}<table><tbody><tr><td>{dash(value)}</td></tr></tbody></table></>);
};

const GenericJsonReport = ({ fetcher, batchNumber, title }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!batchNumber || typeof fetcher !== 'function') return;
    let active = true;
    setLoading(true); setError(''); setData(null);
    fetcher(
      { batch_number: batchNumber },
      (res) => {
        if (!active) return;
        if (res?.data?.status === 'success' && res?.data?.data) setData(res.data.data);
        else setError(res?.data?.message || 'No report data available');
        setLoading(false);
      },
      () => { if (active) { setError('Failed to load report'); setLoading(false); } }
    );
    return () => { active = false; };
  }, [batchNumber, fetcher]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" tip="Generating report…" /></div>;
  if (error || !data) return <Empty description={error || 'No report'} style={{ padding: 60 }} />;

  return (
    <div>
      <style>{REPORT_STYLE}</style>
      <div className="no-print" style={{ textAlign: 'right', marginBottom: 8 }}>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => printReport(rootRef.current, title)}>
          Print / Download
        </Button>
      </div>
      <div ref={rootRef} className="bmr-report">
        <div className="container">
          <header><h1>{title || 'Report'}</h1></header>
          {Object.entries(data).map(([k, v]) => <Section key={k} name={k} value={v} />)}
        </div>
      </div>
    </div>
  );
};

export default GenericJsonReport;
